import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import useEnv from "../src/useEnv";
import { useEnvironmentFlag } from "../src/useFlag";
import usePersistedEnvironmentState from "../src/usePersistedEnvironmentState";

export const AGENT_LANDING_PATH = "/agent-app/dashboard";

/**
 * Syncs agent mode state based on feature flags
 * - If oso-for-agents-ui flag is set, agent mode is forced to true
 * - Otherwise, if oso-for-apps-and-agents-ui flag is not set, agent mode is set to false
 */
export function useAgentModeFromFlags() {
  const [isAgentMode, setIsAgentMode] =
    usePersistedEnvironmentState("is_mi6_mode");
  const showOnlyOsoForAgentsFlag = useEnvironmentFlag("oso-for-agents-ui");
  const showOsoForAppsAndAgentsFlag = useEnvironmentFlag(
    "oso-for-apps-and-agents-ui"
  );
  const agentLandingPath = AGENT_LANDING_PATH;

  useEffect(() => {
    if (showOnlyOsoForAgentsFlag) {
      setIsAgentMode(true);
    } else if (!showOsoForAppsAndAgentsFlag) {
      setIsAgentMode(false);
    }
  }, [showOnlyOsoForAgentsFlag, showOsoForAppsAndAgentsFlag, setIsAgentMode]);

  return {
    isAgentMode,
    setIsAgentMode,
    showOnlyOsoForAgentsFlag,
    showOsoForAppsAndAgentsFlag,
    agentLandingPath,
  };
}

/**
 * Guards routes based on agent mode
 * - Redirects to /agent-app/dashboard if on non-agent route but in agent mode
 * - Redirects to /dashboard if on agent route but not in agent mode
 * - Returns whether the current page can be viewed
 * - Waits for environment to load before running guard to avoid incorrect redirects on page refresh
 */
export function useAgentModeGuard() {
  const router = useRouter();
  const [canViewPage, setCanViewPage] = useState(false);
  const { selectedEnv } = useEnv();
  const { isAgentMode } = useAgentModeFromFlags();

  useEffect(() => {
    // Wait for environment to load before running guard
    // This prevents incorrect redirects when refreshing on agent pages
    if (!selectedEnv) {
      return;
    }

    const path = router.pathname;
    if (!path.startsWith("/agent-app") && isAgentMode) {
      setCanViewPage(false);
      router.replace(AGENT_LANDING_PATH);
    } else if (path.startsWith("/agent-app") && !isAgentMode) {
      setCanViewPage(false);
      router.replace("/dashboard");
    } else {
      setCanViewPage(true);
    }
  }, [isAgentMode, router, selectedEnv]);

  // Allow page to render while environment is loading
  if (!selectedEnv) {
    return true;
  }

  return canViewPage;
}
